import { computed, toValue } from "vue"
import { startOfMonth, endOfMonth, format } from "date-fns"
import { GRANULARITY, MARKET_FIELDS } from "@/helpers/energyBillConstants.js"

function marketSum(record) {
  let value = null
  let stable = true
  for (const field of MARKET_FIELDS) {
    const entry = record[field]
    if (entry?.value != null) {
      value = (value ?? 0) + entry.value
      stable = stable && entry.stable !== false
    }
  }
  if (value == null) {
    return null
  }
  return { value, stable }
}

function pointFor(record) {
  const sum = marketSum(record)
  if (sum == null) {
    return null
  }
  return sum.stable ? { totaal: sum.value } : { totaalProvisional: sum.value }
}

/**
 * Maps a record set to chart points keyed by datetime. When dayKey is given,
 * only records falling on that day are included (used for the quarter view).
 */
function buildPoints(records, dayKey = null) {
  const result = {}
  if (!records) {
    return result
  }
  for (const [datetime, record] of Object.entries(records)) {
    if (!dayKey || format(new Date(datetime), "yyyy-MM-dd") === dayKey) {
      const point = pointFor(record.data)
      if (point) {
        result[datetime] = point
      }
    }
  }
  return result
}

/**
 * Builds the data, series and axis configuration for the "marktresultaten"
 * timeseries chart. Aggregates all known market-result fields per period into
 * a single (definitive vs. provisional) stack.
 */
export default function useMarketChart({
  monthRecord,
  selectedMonth,
  granularity,
  dailyData,
  quarterData,
  effectiveDayKey,
}) {
  const chartSeries = computed(() => {
    const series = {
      totaal: {
        label: "Marktresultaten",
        axis: "finance",
        color: "finance",
        stack: "total",
      },
      totaalProvisional: {
        label: "Marktresultaten (voorlopig)",
        axis: "finance",
        secondary: true,
        color: "finance",
        stack: "total",
      },
    }
    if (toValue(granularity) === GRANULARITY.QUARTER) {
      delete series.totaal.stack
      delete series.totaalProvisional.stack
    }
    return series
  })

  const chartAxes = {
    finance: { title: "Financieel", format: "€ _", precision: 0 },
  }

  const chartData = computed(() => {
    const dayKey = toValue(effectiveDayKey)
    if (toValue(granularity) === GRANULARITY.QUARTER) {
      if (!dayKey) {
        return {}
      }
      return buildPoints(toValue(quarterData)?.records, dayKey)
    }
    return buildPoints(toValue(dailyData)?.records)
  })

  const chartLimits = computed(() => {
    const dayKey = toValue(effectiveDayKey)
    if (toValue(granularity) === GRANULARITY.QUARTER && dayKey) {
      return [new Date(`${dayKey}T00:00:00`), new Date(`${dayKey}T23:59:59`)]
    }
    const month = toValue(selectedMonth)
    return [startOfMonth(month), endOfMonth(month)]
  })

  const chartTimeUnit = computed(() =>
    toValue(granularity) === GRANULARITY.QUARTER ? "hour" : "day",
  )

  const chartType = computed(() =>
    toValue(granularity) === GRANULARITY.QUARTER ? "step" : "bar",
  )

  const monthMarketSum = computed(() => {
    const record = toValue(monthRecord)
    return record ? marketSum(record) : null
  })
  const marketTotal = computed(() => monthMarketSum.value?.value ?? null)
  const isMarketDefinitive = computed(
    () => monthMarketSum.value?.stable === true,
  )

  return {
    chartSeries,
    chartAxes,
    chartData,
    chartLimits,
    chartTimeUnit,
    chartType,
    marketTotal,
    isMarketDefinitive,
  }
}
